import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Globe, Plus, RefreshCw, Edit2, Trash2, RotateCcw, X, Star, AlertTriangle, MapPin, LocateFixed } from 'lucide-react';
import { locationAPI } from '../services/api';
import { useToast, useConfirm } from './UIProvider';
import { setClinicTimezone } from '../utils/clinicTime';
import RowActionsMenu from './RowActionsMenu';
import SearchableSelect from './SearchableSelect';
import useDialog from '../hooks/useDialog';
import TablePager, { usePagedRows } from './TablePager';

// Curated IANA zones (Pakistan + common regions staff might relocate a branch to). The select
// allows typing any other valid IANA id — the backend validates it.
const TIMEZONE_OPTIONS = [
  'Asia/Karachi',
  'Asia/Kabul',
  'Asia/Dubai',
  'Asia/Riyadh',
  'Asia/Tehran',
  'Asia/Kolkata',
  'Asia/Dhaka',
  'Asia/Kathmandu',
  'Asia/Colombo',
  'Asia/Singapore',
  'Asia/Hong_Kong',
  'Asia/Tokyo',
  'Europe/London',
  'Europe/Paris',
  'Europe/Istanbul',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Australia/Sydney',
  'Africa/Cairo',
  'UTC',
];

// Live wall-clock + UTC offset for a given zone, so the admin can see what they're choosing.
const describeZone = (tz, now) => {
  try {
    const time = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
    }).format(now);
    const offParts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    }).formatToParts(now);
    const off = offParts.find((p) => p.type === 'timeZoneName')?.value || '';
    return { time, off };
  } catch {
    return { time: '—', off: '' };
  }
};

const TimezoneSettings = ({ hasPermission }) => {
  const notify = useToast();
  const confirm = useConfirm();

  const can = useCallback(
    (action) => (typeof hasPermission === 'function' ? hasPermission('SETTINGS_TIMEZONE', action) : true),
    [hasPermission],
  );

  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [now, setNow] = useState(() => new Date());

  // Tick once a minute so the live clocks stay roughly current.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await locationAPI.getAll();
      setLocations(Array.isArray(data) ? data : []);
    } catch (e) {
      notify('error', 'Failed to load locations');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  // After any change that may alter the default location's zone, refresh the app-wide clinic zone
  // and tell the rest of the app to re-read it so displayed times update without a reload.
  const syncClinicZone = useCallback((loc) => {
    if (loc && loc.isDefault && loc.timezone) {
      setClinicTimezone(loc.timezone);
      window.dispatchEvent(new Event('clinic:timezone-changed'));
    } else {
      window.dispatchEvent(new Event('clinic:timezone-changed'));
    }
  }, []);

  const handleSetDefault = async (loc) => {
    if (!can('EDIT') || loc.isDefault) return;
    const ok = await confirm({
      title: `Make "${loc.name}" the default location?`,
      message: `Its timezone (${loc.timezone}) becomes the clinic-wide clock. New patient registrations, tokens, prescriptions and invoices will be stamped in this zone, and dates across the app will display in it.`,
      confirmText: 'Set as default',
      tone: 'primary',
    });
    if (!ok) return;
    try {
      const updated = await locationAPI.setDefault(loc.id);
      notify('success', `"${loc.name}" is now the default location`);
      syncClinicZone(updated);
      load();
    } catch (e) {
      notify('error', e.message || 'Failed to set default location');
    }
  };

  const handleDeactivate = async (loc) => {
    const ok = await confirm({
      title: `Deactivate "${loc.name}"?`,
      message: 'This location will be hidden from active lists. You can reactivate it any time.',
      confirmText: 'Deactivate',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await locationAPI.update(loc.id, { isActive: false });
      notify('success', `"${loc.name}" deactivated`);
      load();
    } catch (e) {
      notify('error', e.message || 'Failed to deactivate location');
    }
  };

  const handleReactivate = async (loc) => {
    try {
      await locationAPI.update(loc.id, { isActive: true });
      notify('success', `"${loc.name}" reactivated`);
      load();
    } catch (e) {
      notify('error', e.message || 'Failed to reactivate location');
    }
  };

  const handleDelete = async (loc) => {
    const ok = await confirm({
      title: `Delete "${loc.name}"?`,
      message: 'This permanently removes the location. This cannot be undone.',
      confirmText: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await locationAPI.remove(loc.id);
      notify('success', `"${loc.name}" deleted`);
      load();
    } catch (e) {
      notify('error', e.message || 'Failed to delete location');
    }
  };

  const canEdit = can('EDIT');
  const canCreate = can('CREATE');
  const canDelete = can('DELETE');
  const paging = usePagedRows(locations, { id: 'locations' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-50 via-white to-teal-50 p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 rounded-2xl flex items-center justify-center">
              <Clock className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Timezone Settings</h1>
              <p className="text-gray-600">
                Set the timezone for each hospital location. The default location's timezone is used
                across the app - patient registration, tokens, prescriptions, invoices and all timestamps.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canCreate && (
              <button
                onClick={() => setCreating(true)}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              >
                <Plus className="w-4 h-4" />
                <span>Add Location</span>
              </button>
            )}
            <button
              onClick={load}
              className="p-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              title="Refresh"
            >
              <RefreshCw className={`w-5 h-5 text-gray-600 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {!loading && <TablePager paging={paging} noun="location" />}
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase">
                <th className="px-6 py-3">Location</th>
                <th className="px-4 py-3">Timezone</th>
                <th className="px-4 py-3">Local time</th>
                <th className="px-4 py-3">Position</th>
                <th className="px-4 py-3 text-center">Default</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan="7"><div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin"></div></div></td></tr>
              ) : locations.length === 0 ? (
                <tr><td colSpan="7" className="py-12 text-center text-gray-500">
                  No locations yet.{canCreate ? ' Click "Add Location" to create one.' : ''}
                </td></tr>
              ) : paging.pageRows.map((loc) => {
                const { time, off } = describeZone(loc.timezone, now);
                const actions = [];
                if (canEdit) {
                  actions.push({ key: 'edit', label: 'Edit', icon: Edit2, iconClass: 'text-emerald-500', onClick: () => setEditing(loc) });
                  if (!loc.isDefault && loc.isActive) {
                    actions.push({ key: 'default', label: 'Set as default', icon: Star, iconClass: 'text-amber-500', onClick: () => handleSetDefault(loc) });
                  }
                  actions.push(
                    loc.isActive
                      ? { key: 'deact', label: 'Deactivate', icon: Trash2, danger: true, onClick: () => handleDeactivate(loc), disabled: loc.isDefault }
                      : { key: 'react', label: 'Reactivate', icon: RotateCcw, iconClass: 'text-emerald-500', onClick: () => handleReactivate(loc) },
                  );
                }
                if (canDelete && !loc.isDefault) {
                  actions.push({ key: 'delete', label: 'Delete', icon: Trash2, danger: true, onClick: () => handleDelete(loc) });
                }
                return (
                  <tr key={loc.id} className={`hover:bg-gray-50/60 ${!loc.isActive ? 'opacity-60' : ''}`}>
                    <td className="px-6 py-3">
                      <div className="font-semibold text-gray-900">{loc.name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
                        <Globe className="w-3.5 h-3.5 text-gray-400" />
                        {loc.timezone}
                        {off ? <span className="text-xs text-gray-400">({off})</span> : null}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 tabular-nums">{time}</td>
                    <td className="px-4 py-3 text-sm">
                      {loc.latitude != null && loc.longitude != null ? (
                        <span className="inline-flex items-center gap-1.5 text-gray-700">
                          <MapPin className="w-3.5 h-3.5 text-emerald-500" />
                          <span className="tabular-nums">{Number(loc.latitude).toFixed(5)}, {Number(loc.longitude).toFixed(5)}</span>
                          <span className="text-xs text-gray-400">within {loc.radiusMeters || 150} m</span>
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Not set</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {loc.isDefault ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <Star className="w-3 h-3 fill-amber-500 text-amber-500" />
                          Default
                        </span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                        loc.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${loc.isActive ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                        {loc.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {actions.length > 0 ? <RowActionsMenu items={actions} /> : <span className="text-gray-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!loading && <TablePager paging={paging} noun="location" position="bottom" />}
        </div>

        {!canEdit && !loading && (
          <p className="text-xs text-gray-500 italic px-1">You don't have permission to change timezone settings.</p>
        )}
      </div>

      {(creating || editing) && (
        <LocationFormModal
          existing={editing}
          now={now}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={(saved) => {
            setCreating(false);
            setEditing(null);
            syncClinicZone(saved);
            load();
          }}
        />
      )}
    </div>
  );
};

const LocationFormModal = ({ existing, now, onClose, onSaved }) => {
  const notify = useToast();
  const isEdit = !!existing;
  const [name, setName] = useState(existing?.name || '');
  const [timezone, setTimezone] = useState(existing?.timezone || 'Asia/Karachi');
  const [makeDefault, setMakeDefault] = useState(existing?.isDefault || false);
  // Where the hospital is, for attendance: punches made further than the radius are flagged.
  const [latitude, setLatitude] = useState(existing?.latitude != null ? String(existing.latitude) : '');
  const [longitude, setLongitude] = useState(existing?.longitude != null ? String(existing.longitude) : '');
  const [radius, setRadius] = useState(existing?.radiusMeters != null ? String(existing.radiusMeters) : '150');
  const [locating, setLocating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const locationDialogRef = useDialog(() => { if (!saving) onClose(); });

  const useMyPosition = () => {
    if (!navigator.geolocation) { setError('This browser cannot find your position.'); return; }
    setLocating(true);
    setError('');
    navigator.geolocation.getCurrentPosition(
      (fix) => {
        setLatitude(fix.coords.latitude.toFixed(6));
        setLongitude(fix.coords.longitude.toFixed(6));
        setLocating(false);
      },
      () => { setError('Your position could not be found. Allow location access and try again, or type the coordinates.'); setLocating(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  // The position is optional, but half a position is not.
  const positionProblem = () => {
    if (latitude === '' && longitude === '') return null;
    const lat = Number(latitude);
    const lng = Number(longitude);
    const r = Number(radius);
    if (latitude === '' || longitude === '' || Number.isNaN(lat) || Number.isNaN(lng)) return 'Enter both the latitude and the longitude, or leave both empty.';
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return 'The coordinates are out of range.';
    if (radius === '' || Number.isNaN(r) || r < 20 || r > 5000) return 'The allowed distance must be between 20 and 5000 metres.';
    return null;
  };

  const preview = describeZone(timezone, now);
  const options = TIMEZONE_OPTIONS.includes(timezone) ? TIMEZONE_OPTIONS : [timezone, ...TIMEZONE_OPTIONS];

  const handleSubmit = async () => {
    if (!name.trim()) { setError('Location name is required'); return; }
    if (!timezone.trim()) { setError('Timezone is required'); return; }
    const posProblem = positionProblem();
    if (posProblem) { setError(posProblem); return; }
    setError('');
    setSaving(true);
    const position = latitude === '' && longitude === ''
      ? { latitude: null, longitude: null, radiusMeters: null }
      : { latitude: Number(latitude), longitude: Number(longitude), radiusMeters: Number(radius) };
    try {
      let saved;
      if (isEdit) {
        saved = await locationAPI.update(existing.id, { name: name.trim(), timezone: timezone.trim(), ...position });
        // Promoting to default is a separate, explicit transition.
        if (makeDefault && !existing.isDefault) {
          saved = await locationAPI.setDefault(existing.id);
        }
      } else {
        saved = await locationAPI.create({ name: name.trim(), timezone: timezone.trim(), makeDefault, ...position });
      }
      notify('success', isEdit ? 'Location updated' : 'Location created');
      onSaved(saved);
    } catch (e) {
      setError(e.message || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div ref={locationDialogRef} className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      {/* No overflow-hidden on the shell — the Timezone picker inside is a long searchable list and
          needs to be able to extend past it. Corners are rounded on the header and footer instead. */}
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="bg-gradient-to-r from-navy-800 to-navy-700 p-5 flex items-center justify-between rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              {isEdit ? <Edit2 className="w-4 h-4 text-white" /> : <Plus className="w-4 h-4 text-white" />}
            </div>
            <h2 className="text-lg font-bold text-white">{isEdit ? 'Edit Location' : 'New Location'}</h2>
          </div>
          <button aria-label="Close" onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-1">Name <span className="text-red-500">*</span></label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={150}
              className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
              placeholder="e.g. Perveen Aslam Memorial Hospital"
              autoFocus
            />
          </div>
          <div>
            <SearchableSelect
              label="Timezone *"
              value={timezone}
              onChange={setTimezone}
              options={options}
              placeholder="Select timezone"
              allowCustom
              icon={<Globe className="w-4 h-4" />}
            />
            <p className="mt-1.5 text-xs text-gray-500">
              Current time there: <span className="font-medium text-gray-700">{preview.time}</span>
              {preview.off ? ` (${preview.off})` : ''}
            </p>
          </div>
          <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={makeDefault}
              disabled={isEdit && existing?.isDefault}
              onChange={(e) => setMakeDefault(e.target.checked)}
              className="mt-0.5 rounded border-gray-300 focus:ring-emerald-500"
            />
            <span>
              Use as the clinic-wide default timezone
              {isEdit && existing?.isDefault && <span className="text-gray-400"> (already the default)</span>}
            </span>
          </label>
          <div className="rounded-xl border border-gray-200 p-3 space-y-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-sm font-semibold flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gray-500" /> Position (for attendance)</span>
              <button type="button" onClick={useMyPosition} disabled={locating || saving} className="text-xs font-medium text-navy-700 hover:underline flex items-center gap-1 disabled:opacity-60">
                <LocateFixed className="w-3.5 h-3.5" /> {locating ? 'Finding…' : 'Use my current position'}
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label htmlFor="loc-lat" className="block text-xs text-gray-600 mb-1">Latitude</label>
                <input id="loc-lat" value={latitude} onChange={(e) => setLatitude(e.target.value)} inputMode="decimal" placeholder="31.5204" className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label htmlFor="loc-lng" className="block text-xs text-gray-600 mb-1">Longitude</label>
                <input id="loc-lng" value={longitude} onChange={(e) => setLongitude(e.target.value)} inputMode="decimal" placeholder="74.3587" className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
              <div>
                <label htmlFor="loc-radius" className="block text-xs text-gray-600 mb-1">Within (m)</label>
                <input id="loc-radius" value={radius} onChange={(e) => setRadius(e.target.value)} inputMode="numeric" className="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500" />
              </div>
            </div>
            <p className="text-xs text-gray-500">Staff punches made further than this from the hospital are flagged for review. Press the button while standing at the hospital, or leave it empty to skip the check.</p>
          </div>
          {makeDefault && (
            <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                All new timestamps (registrations, tokens, prescriptions, invoices) and date displays
                across the app will use this zone.
              </span>
            </div>
          )}
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
        </div>
        <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2 rounded-b-2xl">
          <button onClick={onClose} disabled={saving} className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm font-medium">Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="px-5 py-2 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create location')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimezoneSettings;
